/**
 * Random Palette — seeded algorithmic colour harmony.
 *
 * Distinct from Random Madras (curated Madras swatch pool). This generator
 * explores a broader colour space with constrained HSL relationships so
 * results feel surprisingly good most of the time, without a full
 * colour-science system. Same seed, same palette. Uses seeded PRNG only.
 */

import { createSeededRandom } from './randomMadrasPalette.ts';
import { COLOR_LIMIT_MIN, normalizeColorLimit, resolvePaletteGenerationCount } from './colorLimit.ts';

export const RANDOM_PALETTE_RNG_NAMESPACE = 'random-palette';

export type HarmonyStrategy =
  | 'analogous'
  | 'complementary'
  | 'splitComplementary'
  | 'triadic'
  | 'tetradic'
  | 'monochromatic';

const STRATEGIES: readonly HarmonyStrategy[] = [
  'analogous',
  'complementary',
  'splitComplementary',
  'triadic',
  'tetradic',
  'monochromatic',
];

type Hsl = { h: number; s: number; l: number };

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function wrapHue(h: number): number {
  const x = h % 360;
  return x < 0 ? x + 360 : x;
}

function hslToHex(h: number, s: number, l: number): string {
  const hh = wrapHue(h) / 360;
  const ss = clamp01(s);
  const ll = clamp01(l);
  if (ss === 0) {
    const v = Math.round(ll * 255);
    return `#${v.toString(16).padStart(2, '0').repeat(3)}`;
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = ll < 0.5 ? ll * (1 + ss) : ll + ss - ll * ss;
  const p = 2 * ll - q;
  const r = Math.round(hue2rgb(p, q, hh + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, hh) * 255);
  const b = Math.round(hue2rgb(p, q, hh - 1 / 3) * 255);
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

function pickStrategy(random: () => number): HarmonyStrategy {
  return STRATEGIES[Math.floor(random() * STRATEGIES.length)];
}

/** Hue offsets (degrees) relative to the base hue for a strategy. */
function strategyOffsets(strategy: HarmonyStrategy, count: number, random: () => number): number[] {
  const offsets: number[] = [0];
  switch (strategy) {
    case 'analogous': {
      const step = 18 + random() * 22; // 18–40°
      for (let i = 1; i < count; i++) {
        const side = i % 2 === 1 ? 1 : -1;
        offsets.push(side * step * Math.ceil(i / 2));
      }
      break;
    }
    case 'complementary': {
      offsets.push(180);
      for (let i = 2; i < count; i++) {
        offsets.push(i % 2 === 0 ? random() * 24 - 12 : 180 + random() * 24 - 12);
      }
      break;
    }
    case 'splitComplementary': {
      const split = 150 + random() * 20; // ~150–170
      offsets.push(split, 360 - split);
      for (let i = 3; i < count; i++) {
        offsets.push(i % 3 === 0 ? random() * 16 - 8 : i % 3 === 1 ? split + random() * 16 - 8 : 360 - split + random() * 16 - 8);
      }
      break;
    }
    case 'triadic': {
      offsets.push(120, 240);
      for (let i = 3; i < count; i++) {
        offsets.push((i * 120) % 360 + random() * 14 - 7);
      }
      break;
    }
    case 'tetradic': {
      offsets.push(90, 180, 270);
      for (let i = 4; i < count; i++) {
        offsets.push((i * 90) % 360 + random() * 12 - 6);
      }
      break;
    }
    case 'monochromatic':
    default: {
      for (let i = 1; i < count; i++) {
        offsets.push(random() * 16 - 8); // tiny hue drift only
      }
      break;
    }
  }
  while (offsets.length < count) {
    offsets.push(random() * 360);
  }
  return offsets.slice(0, count);
}

function varySaturation(strategy: HarmonyStrategy, random: () => number, index: number): number {
  if (strategy === 'monochromatic') {
    return 0.25 + random() * 0.55;
  }
  // Accents a bit punchier; supporting colours calmer.
  if (index === 0) return 0.45 + random() * 0.4;
  if (index === 1) return 0.35 + random() * 0.45;
  return 0.28 + random() * 0.5;
}

function varyLightness(strategy: HarmonyStrategy, random: () => number, index: number, count: number): number {
  if (strategy === 'monochromatic') {
    // Spread lightness so mono palettes stay distinct.
    const t = count <= 1 ? 0.5 : index / (count - 1);
    return 0.22 + t * 0.55 + (random() * 0.06 - 0.03);
  }
  // Avoid muddy mid-greys: prefer a mix of deeper and brighter.
  const bands = [0.28, 0.42, 0.55, 0.68, 0.78];
  const base = bands[index % bands.length];
  return clamp01(base + random() * 0.1 - 0.05);
}

function colourDistance(a: Hsl, b: Hsl): number {
  const dh = Math.min(Math.abs(a.h - b.h), 360 - Math.abs(a.h - b.h)) / 180;
  const ds = Math.abs(a.s - b.s);
  const dl = Math.abs(a.l - b.l);
  return dh * 0.55 + ds * 0.2 + dl * 0.45;
}

function ensureSeparation(colours: Hsl[], strategy: HarmonyStrategy, random: () => number): Hsl[] {
  if (strategy === 'monochromatic') return colours;
  const minDist = 0.14;
  const out = colours.map((c) => ({ ...c }));
  for (let i = 1; i < out.length; i++) {
    let attempts = 0;
    while (attempts < 6) {
      const tooClose = out.slice(0, i).some((prev) => colourDistance(prev, out[i]) < minDist);
      if (!tooClose) break;
      out[i].h = wrapHue(out[i].h + 20 + random() * 40);
      out[i].l = clamp01(out[i].l + (random() > 0.5 ? 0.08 : -0.08));
      attempts += 1;
    }
  }
  return out;
}

function maybeAddNeutral(colours: Hsl[], random: () => number): Hsl[] {
  // Occasional soft neutral / off-white / charcoal — not forced every time.
  if (colours.length < 3 || random() > 0.28) return colours;
  const neutrals: Hsl[] = [
    { h: 40, s: 0.06, l: 0.94 },
    { h: 210, s: 0.04, l: 0.92 },
    { h: 30, s: 0.05, l: 0.88 },
    { h: 0, s: 0.02, l: 0.18 },
    { h: 220, s: 0.05, l: 0.22 },
  ];
  const neutral = neutrals[Math.floor(random() * neutrals.length)];
  const copy = colours.map((c) => ({ ...c }));
  copy[copy.length - 1] = neutral;
  return copy;
}

export type CreateRandomHarmonyPaletteOpts = {
  seed?: string | null;
  colorLimitMode?: string | null;
  colorLimit?: number | null;
  /** Explicit count override (already clamped). */
  count?: number;
};

/**
 * Deterministic Random Palette for one artwork seed.
 * Count follows the existing colour-limit controls; minimum is always 2.
 */
export function createRandomHarmonyPalette(opts: CreateRandomHarmonyPaletteOpts = {}): string[] {
  const seed = opts.seed == null ? '' : String(opts.seed);
  const random = createSeededRandom(seed, RANDOM_PALETTE_RNG_NAMESPACE);

  const count =
    opts.count != null
      ? normalizeColorLimit(opts.count)
      : resolvePaletteGenerationCount({
          colorLimitMode: opts.colorLimitMode,
          colorLimit: opts.colorLimit,
          random,
        });

  const strategy = pickStrategy(random);
  const baseHue = random() * 360;
  const offsets = strategyOffsets(strategy, count, random);

  let colours: Hsl[] = offsets.map((offset, index) => ({
    h: wrapHue(baseHue + offset),
    s: varySaturation(strategy, random, index),
    l: varyLightness(strategy, random, index, count),
  }));

  colours = ensureSeparation(colours, strategy, random);
  colours = maybeAddNeutral(colours, random);

  // Final safety: never return fewer than the minimum.
  while (colours.length < COLOR_LIMIT_MIN) {
    colours.push({
      h: wrapHue(baseHue + colours.length * 40),
      s: 0.4 + random() * 0.3,
      l: 0.35 + random() * 0.3,
    });
  }

  return colours.slice(0, count).map((c) => hslToHex(c.h, c.s, c.l));
}
